import React, { useEffect, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import { adminApi } from "../../../api/adminApi";
import "./StoreCompletionReminderLog.css";

const PAGE_SIZE = 25;

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown date" : date.toLocaleString();
};

const StoreCompletionReminderLog = ({ refreshKey = 0 }) => {
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    reminderType: "",
    fromDate: "",
    toDate: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [page, setPage] = useState(1);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    adminApi
      .getStoreCompletionReminderLogs({
        ...appliedFilters,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((response) => {
        if (cancelled) return;
        const data = response.data || {};
        setLogs(data.items || []);
        setTotal(Number(data.total) || 0);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(
            loadError.response?.data?.error ||
              "Could not load the store reminder history.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [appliedFilters, page, refreshKey, refreshing]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstResult = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastResult = Math.min(page * PAGE_SIZE, total);

  return (
    <section className="es-card scr-log" aria-labelledby="scr-log-heading">
      <div className="es-card-head es-card-head-row">
        <div>
          <h2 id="scr-log-heading">Reminder history</h2>
          <p>
            See automatic, manual, and payment reminders, including email
            sending failures. “Sent” means the email service accepted the
            message.
          </p>
        </div>
        <button
          className="es-btn"
          type="button"
          onClick={() => setRefreshing((current) => current + 1)}
          disabled={loading}
        >
          <RefreshCw size={15} aria-hidden="true" />
          Refresh
        </button>
      </div>

      <form className="scr-log-filters" onSubmit={applyFilters}>
        <label className="scr-log-search">
          Search
          <span>
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              value={filters.search}
              maxLength={200}
              placeholder="Client, store, or email"
              onChange={(event) => updateFilter("search", event.target.value)}
            />
          </span>
        </label>
        <label>
          Result
          <select
            value={filters.status}
            onChange={(event) => updateFilter("status", event.target.value)}
          >
            <option value="">All results</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        </label>
        <label>
          Reminder
          <select
            value={filters.reminderType}
            onChange={(event) =>
              updateFilter("reminderType", event.target.value)
            }
          >
            <option value="">All reminders</option>
            <option value="automatic">Automatic</option>
            <option value="manual">Store completion (manual)</option>
            <option value="payment">Payment</option>
            <option value="website">Website</option>
          </select>
        </label>
        <label>
          From
          <input
            type="date"
            value={filters.fromDate}
            onChange={(event) => updateFilter("fromDate", event.target.value)}
          />
        </label>
        <label>
          To
          <input
            type="date"
            value={filters.toDate}
            onChange={(event) => updateFilter("toDate", event.target.value)}
          />
        </label>
        <button className="es-btn es-btn-primary" type="submit">
          Apply filters
        </button>
      </form>

      {error && (
        <div className="scr-log-error" role="alert">
          {error}
        </div>
      )}

      <div className="scr-log-table-wrap" aria-busy={loading}>
        <table className="scr-log-table">
          <thead>
            <tr>
              <th scope="col">Client</th>
              <th scope="col">Store / project</th>
              <th scope="col">Reminder</th>
              <th scope="col">Result</th>
              <th scope="col">Subject</th>
              <th scope="col">Attempted</th>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              logs.map((log) => (
                <tr key={log.ReminderLogID}>
                  <td>
                    <strong>{log.DisplayName || "Client"}</strong>
                    <span>{log.RecipientEmail || "No email address"}</span>
                  </td>
                  <td>{log.StoreName || "Store name not set"}</td>
                  <td className="scr-log-capitalize">
                    {log.ReminderType === "payment"
                      ? "Payment"
                      : log.ReminderType === "website"
                        ? "Website"
                        : log.ReminderType === "manual"
                          ? "Store completion"
                          : log.ReminderType}
                  </td>
                  <td>
                    <span
                      className={`scr-log-status is-${log.Status === "sent" ? "sent" : "failed"}`}
                    >
                      {log.Status === "sent" ? "Sent" : "Failed"}
                    </span>
                    {log.ErrorMessage && (
                      <span className="scr-log-failure" title={log.ErrorMessage}>
                        {log.ErrorMessage}
                      </span>
                    )}
                  </td>
                  <td className="scr-log-subject" title={log.Subject}>
                    {log.Subject}
                  </td>
                  <td>{formatDate(log.AttemptedAt)}</td>
                </tr>
              ))}
            {!loading && !logs.length && !error && (
              <tr>
                <td className="scr-log-empty" colSpan="6">
                  No reminder attempts match these filters.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td className="scr-log-empty" colSpan="6">
                  Loading reminder history...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <footer className="scr-log-pagination">
        <span>
          {total ? `Showing ${firstResult}–${lastResult} of ${total}` : "0 results"}
        </span>
        <div>
          <button
            className="es-btn"
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={loading || page <= 1}
          >
            Previous
          </button>
          <span>
            Page {page} of {pageCount}
          </span>
          <button
            className="es-btn"
            type="button"
            onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
            disabled={loading || page >= pageCount}
          >
            Next
          </button>
        </div>
      </footer>
    </section>
  );
};

export default StoreCompletionReminderLog;
